/**
 * SVD field types for the storefront buy box. There is **no separate `SvdProduct` type** — the SVD
 * product IS the existing `Product` domain, which carries the SVD fields directly (`IProduct.vdas`,
 * `attributeMap`, `attributeValues`, `media`, `swatchProductIds`, `swatchProducts`), and the variant
 * fields sit on `BaseProductVariant` (`attributeValues`, `tags`, `media`). This file only defines the
 * *shapes of those fields*, typed loosely with `[key: string]: unknown` so the raw staging JSON conforms
 * without transcribing every field. See `ai-guidelines/project/svd/response-jsons/product-data-api.json`.
 *
 * The SVD-behaviour types the flow consumes — the `VdaType` render enum and the `SvdSelection`
 * selection state — also live here so every consumer (selector block, shared product form, widget,
 * cascade helper) imports them from one shared place.
 */

/** VDA render category — drives the SVD selector dispatch. Wire value is the raw string. */
export enum VdaType {
  SWATCH = 'SWATCH',
  MEASUREMENT = 'MEASUREMENT',
  SELECT = 'SELECT',
}

/**
 * SVD-native variant selection state — the SVD flow's own selection shape, deliberately kept separate
 * from the legacy `ProductVariantOption` tuple (no adaptation, no mixing). One entry per selected VDA:
 * `vdaKey` matches `SvdVda.key`, `valueKey` matches the chosen `SvdVdaValue.key`.
 */
export interface SvdSelection {
  vdaKey: string
  valueKey: string
}

export interface SvdVdaValue {
  key: string // stable identifier — matching / URL
  value: string // canonical value
  localisedValue: string // display
  order?: number // display sequence for the value (XS,S,M,L…) — use this for rendering order
  sortOrder?: number // backend size-code (MEASUREMENT); NOT display order (runs opposite) — do not sort by it
  hexCode?: string // swatch (HEX_COLOR)
  swatchType?: string // 'HEX_COLOR' | 'IMAGE'
  swatchImageUrl?: string // swatch (IMAGE) image url — the field SAND actually populates for image swatches
  [key: string]: unknown
}

export interface SvdVda {
  key: string // "color", "size", "ringSize"
  name: string // "Color", "Ring Size" (localised label)
  type: string // VdaType on the wire
  order: number // display + cascade sequence
  values: SvdVdaValue[]
  [key: string]: unknown
}

/**
 * Variant-level attribute value — the per-variant VDA map (`variant.attributeValues[vdaKey].key` is the
 * value the SVD flow matches selections against). Non-VDA attribute entries can also appear here.
 */
export interface SvdVariantAttributeValue {
  key?: string
  value?: string
  localisedValue?: string
  type?: string
  [key: string]: unknown
}

/** Variant-level tag (sample: `{ value: "Sold Out", displayName, options: [], vendor_type: null }`). */
export interface SvdVariantTag {
  value?: string
  displayName?: string
  options?: unknown[]
  vendor_type?: string | null
  [key: string]: unknown
}

/**
 * Variant-level media — **asset-id arrays** (`images: ["assetId", ...]`), unlike the product-level
 * `SvdMedia` pool which is asset-id-keyed (Record). Different shape on the wire at each level.
 */
export interface SvdVariantMedia {
  images?: string[]
  videos?: string[]
  shortVideos?: string[]
  experimentVideos?: string[]
  experimentImages?: string[]
  [key: string]: unknown
}

/** Declares which attribute keys resolve at product vs variant level (SVD `attributeMap`). */
export interface SvdAttributeMap {
  product: string[]
  variant: string[]
  [key: string]: unknown
}

/**
 * Product-level attribute value — flattened shape (Gap 10): `{ type, name, localisedValue, key? }`.
 * An entry may be `null` when the attribute is unset.
 */
export interface SvdAttributeValue {
  type?: string
  name?: string
  key?: string
  localisedValue?: string
  value?: unknown
  values?: unknown[]
  [key: string]: unknown
}

/**
 * Swatch sibling product (product-level `swatchProducts[]`) — a rich, product-like object the swatch VDA
 * points at (its own id / title / image pool / variants). Typed loosely; the storefront reads only a few
 * fields, and the rest ride the index signature.
 */
export interface SvdSwatchProduct {
  id?: string | number
  shopifyProductId?: string | number
  contentfulProductId?: string
  title?: string
  images?: Record<string, unknown>
  variants?: unknown[]
  [key: string]: unknown
}

/** SVD media pool — images/videos keyed by asset id, each tagged with the VDA value(s) it applies to. */
export interface SvdMedia {
  images?: Record<string, unknown>
  videos?: Record<string, unknown>
  shortVideos?: Record<string, unknown>
  [key: string]: unknown
}

